"use client";

import { useSession, useSupabaseClient } from "@/components/SupabaseProvider";
import { useQueryClient } from "@tanstack/react-query";
import dayjs from "dayjs";
import { usePathname, useRouter } from "next/navigation";
import {
  ComponentPropsWithoutRef,
  FC,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import toast from "react-hot-toast";
import {
  PLAN_TO_CREDIT_COUNT,
  PLAN_TO_EXPORT_COUNT_LIMIT,
  PLAN_TO_LABEL,
  PLAN_TO_VIDEO_COUNT_LIMIT,
  PlanType,
} from "../const/planConst";
import { BillingHelper } from "../helpers/billing";
import { ModalContext } from "../hooks/useModal";
import {
  fetchProfileByUserId,
  PROFILE_QUERY_KEY,
  useProfile,
} from "../hooks/useProfile";
import { useSubscriptionUpcoming } from "../hooks/useSubscriptionPreview";
import { useSubscriptionUpdate } from "../hooks/useSubscriptionUpdate";
import ConfirmAction from "./ConfirmAction";
import Typography from "./Typography";

// Define plan limits with proper typing to match PlanType
const PLAN_LIMITS: Record<
  PlanType,
  {
    videos: number;
    clips: number;
    exports: number;
    credits: number;
  }
> = {
  basic: {
    videos: PLAN_TO_VIDEO_COUNT_LIMIT.get("basic") ?? 5,
    clips: 100,
    exports: PLAN_TO_EXPORT_COUNT_LIMIT.get("basic") ?? 15,
    credits: PLAN_TO_CREDIT_COUNT.get("basic") ?? 300,
  },
  pro: {
    videos: PLAN_TO_VIDEO_COUNT_LIMIT.get("pro") ?? 15,
    clips: 300,
    exports: PLAN_TO_EXPORT_COUNT_LIMIT.get("pro") ?? 35,
    credits: PLAN_TO_CREDIT_COUNT.get("pro") ?? 900,
  },
  "pro-plus": {
    videos: PLAN_TO_VIDEO_COUNT_LIMIT.get("pro-plus") ?? 45,
    clips: 1000,
    exports: PLAN_TO_EXPORT_COUNT_LIMIT.get("pro-plus") ?? 100,
    credits: PLAN_TO_CREDIT_COUNT.get("pro-plus") ?? 2700,
  },
  "basic-yearly": {
    videos: PLAN_TO_VIDEO_COUNT_LIMIT.get("basic") ?? 5,
    clips: 100,
    exports: PLAN_TO_EXPORT_COUNT_LIMIT.get("basic") ?? 15,
    credits: PLAN_TO_CREDIT_COUNT.get("basic") ?? 300,
  },
  "pro-yearly": {
    videos: PLAN_TO_VIDEO_COUNT_LIMIT.get("pro") ?? 15,
    clips: 300,
    exports: PLAN_TO_EXPORT_COUNT_LIMIT.get("pro") ?? 35,
    credits: PLAN_TO_CREDIT_COUNT.get("pro") ?? 900,
  },
  "pro-plus-yearly": {
    videos: PLAN_TO_VIDEO_COUNT_LIMIT.get("pro-plus") ?? 45,
    clips: 1000,
    exports: PLAN_TO_EXPORT_COUNT_LIMIT.get("pro-plus") ?? 100,
    credits: PLAN_TO_CREDIT_COUNT.get("pro-plus") ?? 2700,
  },
  "metered-api": {
    videos: 0,
    clips: 0,
    exports: 0,
    credits: 0,
  },
};

// Minimum clips reserved for a new video-to-shorts submit (matches API
// ONESHOT_MIN_CLIP_COUNT). Used only for leftover_clips copy.
const MIN_CLIPS_PER_NEW_VIDEO = 10;

// TODO: Show the real limit from profile instead of theoretical limits
const getVariantData = (
  targetPlan: PlanType,
  currentPlan?: PlanType,
  upcoming?: any,
  remainingClips?: number,
) => {
  // Ensure we're using a supported plan for limits
  const safeCurrentPlan =
    currentPlan && PLAN_LIMITS[currentPlan]
      ? currentPlan
      : ("basic" as PlanType);

  const safeTargetPlan =
    targetPlan && PLAN_LIMITS[targetPlan] ? targetPlan : ("pro" as PlanType);

  const leftoverClipsLabel =
    remainingClips === 1 ? "1 clip" : `${remainingClips ?? 0} clips`;

  return {
    default: {
      title: "You discovered a Pro feature!",
      description: (
        <>
          Upgrade to{" "}
          <a className="text-violet-500" href="/pricing" target="_blank">
            {PLAN_TO_LABEL.get(safeTargetPlan)}
          </a>{" "}
          to unlock this feature.
        </>
      ),
      confirmLabel: "Upgrade & Continue",
    },
    more_videos: {
      title: "Unlock more input videos",
      description: (
        <>
          {"You've reached your plan's limit of "}
          {PLAN_LIMITS[safeCurrentPlan].videos} input videos per month. Upgrade
          to{" "}
          <a className="text-violet-500" href="/pricing" target="_blank">
            {PLAN_TO_LABEL.get(safeTargetPlan)}
          </a>{" "}
          to process up to {PLAN_LIMITS[safeTargetPlan].videos} input videos per
          month.
        </>
      ),
      confirmLabel: "Upgrade & Continue",
    },
    more_clips: {
      title: "Unlock more clips",
      description: (
        <>
          {"You've reached your plan's limit of "}
          {PLAN_LIMITS[safeCurrentPlan].clips} clips. Upgrade to{" "}
          <a className="text-violet-500" href="/pricing" target="_blank">
            {PLAN_TO_LABEL.get(safeTargetPlan)}
          </a>{" "}
          to generate up to {PLAN_LIMITS[safeTargetPlan].clips} clips.
        </>
      ),
      confirmLabel: "Upgrade & Continue",
    },
    leftover_clips: {
      title: "Not enough clips left for a new video",
      description: (
        <>
          You still have {leftoverClipsLabel} left, but each new video needs at
          least {MIN_CLIPS_PER_NEW_VIDEO}. Upgrade to{" "}
          <a className="text-violet-500" href="/pricing" target="_blank">
            {PLAN_TO_LABEL.get(safeTargetPlan)}
          </a>{" "}
          to get up to {PLAN_LIMITS[safeTargetPlan].clips} clips, or wait until
          your monthly limit resets.
        </>
      ),
      confirmLabel: "Upgrade & Continue",
    },
    more_exports: {
      title: "Unlock more exports",
      description: (
        <>
          {"You've reached your plan's limit of "}
          {PLAN_LIMITS[safeCurrentPlan].exports} exports. Upgrade to{" "}
          <a className="text-violet-500" href="/pricing" target="_blank">
            {PLAN_TO_LABEL.get(safeTargetPlan)}
          </a>{" "}
          to get up to {PLAN_LIMITS[safeTargetPlan].exports} exports/mo.
        </>
      ),
      confirmLabel: "Upgrade & Continue",
    },
    more_credits: {
      title: "Unlock more upload minutes",
      description: (
        <>
          {"You've reached your plan's limit of "}
          {PLAN_LIMITS[safeCurrentPlan].credits} upload minutes. Upgrade to{" "}
          <a className="text-violet-500" href="/pricing" target="_blank">
            {PLAN_TO_LABEL.get(safeTargetPlan)}
          </a>{" "}
          to get up to {PLAN_LIMITS[safeTargetPlan].credits} upload minutes.
        </>
      ),
      confirmLabel: "Upgrade & Continue",
    },
    upload_fair_use: {
      title: "Unlock more uploads",
      description: (
        <>
          {"You've reached your plan's upload fair-use limit. Upgrade to "}
          <a className="text-violet-500" href="/pricing" target="_blank">
            {PLAN_TO_LABEL.get(safeTargetPlan)}
          </a>{" "}
          to continue uploading videos.
        </>
      ),
      confirmLabel: "Upgrade & Continue",
    },
    upload_fonts: {
      title: "Unlock custom fonts",
      description: (
        <>
          {"Upgrade to"}{" "}
          <a className="text-violet-500" href="/pricing" target="_blank">
            {PLAN_TO_LABEL.get(safeTargetPlan)}
          </a>{" "}
          to upload custom fonts.
        </>
      ),
      confirmLabel: "Upgrade & Continue",
    },
    update: {
      title: `Update to ${PLAN_TO_LABEL.get(safeTargetPlan)}`,
      description: <>You can change your plan at any time.</>,
      confirmLabel: "Update",
    },
    upgrade: {
      title: `Upgrade to ${PLAN_TO_LABEL.get(safeTargetPlan)}`,
      description: <>You can change your plan at any time.</>,
      confirmLabel: "Upgrade",
    },
    downgrade: {
      title: `Downgrade to ${PLAN_TO_LABEL.get(safeTargetPlan)}`,
      description: <>Are you sure you want to downgrade your plan?</>,
      confirmLabel: "Downgrade",
    },
    monthly_to_yearly: {
      title: `Switch to yearly`,
      description: <>Save 50% with yearly billing.</>,
      confirmLabel: `Switch to yearly`,
    },
    yearly_to_monthly: {
      title: `Switch to monthly`,
      description: (
        <>
          Your yearly plan stays active until{" "}
          {dayjs(upcoming?.invoice?.period_start * 1000).format("MMM D, YYYY")},
          then renews monthly.
        </>
      ),
      confirmLabel: `Switch to monthly`,
    },
  };
};

type VariantType = keyof ReturnType<typeof getVariantData>;

// Variants that upsell because the current plan is insufficient (limit
// reached / gated feature). Only these auto-close once the profile already
// satisfies the target plan — deliberate plan switches (downgrade, interval
// changes) must not.
const UPSELL_VARIANTS: VariantType[] = [
  "default",
  "more_videos",
  "more_clips",
  "leftover_clips",
  "more_exports",
  "more_credits",
  "upload_fair_use",
  "upload_fonts",
  "upgrade",
];

const PLAN_TIER_ORDER = ["basic", "pro", "pro-plus"];

const planTierRank = (plan?: string | null) =>
  plan ? PLAN_TIER_ORDER.indexOf(plan.replace(/-yearly$/, "")) : -1;

type UpdatePlanProps = {
  onUpdate?: () => void;
  targetPlan: PlanType;
  variant?: VariantType;
  remainingClips?: number;
} & ComponentPropsWithoutRef<"div">;

const ConfirmUpdatePlan: FC<UpdatePlanProps> = ({
  onUpdate,
  targetPlan,
  variant,
  remainingClips,
  ...rest
}) => {
  const session = useSession();
  const { data: profile } = useProfile(3000);
  const modal = useContext(ModalContext);

  // Use profile.plan if provided, otherwise fall back to basic
  const currentPlan = profile?.plan || "basic";

  // Determine if this is an upgrade, downgrade, or lateral move
  const planStatus = useMemo(() => {
    const plans = ["basic", "pro", "pro-plus"] as const;
    const targetPlanBase = targetPlan.replace(/-yearly$/, "") as any;
    const currentPlanBase = currentPlan.replace(/-yearly$/, "") as any;
    const isFromYearlyToMonthly =
      BillingHelper.getPlanInterval(currentPlan) === "yearly" &&
      BillingHelper.getPlanInterval(targetPlan) === "monthly";
    const isFromMonthlyToYearly =
      BillingHelper.getPlanInterval(currentPlan) === "monthly" &&
      BillingHelper.getPlanInterval(targetPlan) === "yearly";

    const targetPlanIndex = plans.indexOf(targetPlanBase);
    const currentPlanIndex = plans.indexOf(currentPlanBase);

    if (isFromYearlyToMonthly) return "yearly_to_monthly";
    if (isFromMonthlyToYearly) return "monthly_to_yearly";
    if (targetPlanIndex > currentPlanIndex) return "upgrade";
    if (targetPlanIndex < currentPlanIndex) return "downgrade";
    return "update"; // Same tier but different billing cycle
  }, [targetPlan, currentPlan]);

  // Determine which variant to use if none is specified
  const effectiveVariant = useMemo(() => {
    if (variant) return variant;
    return planStatus as VariantType;
  }, [variant, planStatus]);

  const { data: upcoming, isLoading: isUpcomingLoading } =
    useSubscriptionUpcoming(targetPlan);
  const subscriptionUpdate = useSubscriptionUpdate();
  const supabaseClient = useSupabaseClient();
  const queryClient = useQueryClient();
  // Keeps the confirm button in a loading state (and Cancel/Esc blocked)
  // while we wait for the webhook-driven profile update after a successful
  // Stripe subscription change.
  const [finalizing, setFinalizing] = useState(false);

  const isUpsellVariant = UPSELL_VARIANTS.includes(effectiveVariant);

  // Safety net: if the profile already satisfies the target plan while an
  // upsell dialog is open (webhook landed while the dialog was up, or the
  // user upgraded in another tab), close it instead of re-rendering an
  // absurd same-plan upsell with a live pay button.
  useEffect(() => {
    if (!isUpsellVariant) return;
    if (subscriptionUpdate.isPending || finalizing) return;
    if (!profile?.plan) return;
    const targetRank = planTierRank(targetPlan);
    if (targetRank >= 0 && planTierRank(profile.plan) >= targetRank) {
      modal.close();
    }
  }, [
    isUpsellVariant,
    subscriptionUpdate.isPending,
    finalizing,
    profile?.plan,
    targetPlan,
    modal,
  ]);

  // PATCH /billing/subscription only updates Stripe; our profile row is
  // written by the webhook. Poll the profile until it reflects the new plan
  // so `onUpdate` retries don't hit a stale 402 and reopen the paywall.
  const waitForPlanPropagation = async (
    timeoutMs = 20000,
    intervalMs = 1500,
  ): Promise<boolean> => {
    const userId = session?.user.id;
    if (!userId) return false;
    const deadline = Date.now() + timeoutMs;
    const targetRank = planTierRank(targetPlan);
    while (Date.now() < deadline) {
      try {
        const fresh = await fetchProfileByUserId(supabaseClient, userId);
        if (fresh.plan === targetPlan || planTierRank(fresh.plan) >= targetRank) {
          queryClient.setQueryData([PROFILE_QUERY_KEY], fresh);
          return true;
        }
      } catch {
        // Transient fetch error — keep polling until the deadline.
      }
      await new Promise((resolve) => setTimeout(resolve, intervalMs));
    }
    return false;
  };

  const router = useRouter();
  const pathname = usePathname();
  const openCheckout = async () => {
    if (!session) return router.push(`/login?redirect=${pathname}`);

    try {
      await subscriptionUpdate.mutateAsync(targetPlan);

      // Only actual tier upgrades apply immediately and gate blocked
      // actions; interval switches / downgrades may be deferred to the next
      // period, so don't hold the dialog waiting for them.
      const isTierUpgrade =
        planTierRank(targetPlan) > planTierRank(currentPlan);
      let propagated = true;
      if (isTierUpgrade) {
        setFinalizing(true);
        try {
          propagated = await waitForPlanPropagation();
        } finally {
          setFinalizing(false);
        }
      }

      modal.close();
      if (propagated) {
        onUpdate?.();
        toast.success(`You've successfully updated to ${targetPlan}!`);
      } else {
        // The Stripe update succeeded but the profile hasn't caught up yet;
        // skip the retry (it would just hit the paywall again).
        toast.success(
          "Plan updated — your new limits may take a moment to apply.",
        );
      }
    } catch (error) {
      console.error(error);
      toast.error("Something went wrong");
    }
  };

  // price.unit_amount is the undiscounted list price; discount_percent is the
  // effective recurring discount (existing subscription discount or the
  // yearly coupon) that will apply to the new plan.
  const discountPercent = upcoming?.discount_percent ?? 0;
  const listPriceCents = upcoming?.price?.unit_amount ?? 0;
  const discountedPriceCents = Math.round(
    listPriceCents * (1 - discountPercent / 100),
  );

  const formatUsd = (cents: number) =>
    `$${(Math.round(cents) / 100).toLocaleString("en-US", {
      maximumFractionDigits: 2,
    })}`;

  const getAdjustmentAmountCents = () => {
    if (!upcoming) return 0;
    if (upcoming.invoice.amount_due <= 0) return 0;
    return discountedPriceCents - upcoming.invoice.amount_due;
  };

  // Get the variant data with the current context (fallback to "default" if variant key is missing)
  const variantMap = getVariantData(
    targetPlan,
    currentPlan,
    upcoming,
    remainingClips,
  );
  const variantData =
    variantMap[effectiveVariant] ?? variantMap.default;

  const isImmediateChange = effectiveVariant === "monthly_to_yearly";
  const isPeriodStartBeforeOrToday =
    isImmediateChange ||
    new Date(upcoming?.invoice.period_start * 1000) <= new Date();

  const interval = BillingHelper.getPlanInterval(targetPlan);

  return (
    <div {...rest} className={`${rest.className}`}>
      <ConfirmAction
        title={variantData.title}
        description={variantData.description}
        confirmLabel={variantData.confirmLabel}
        onConfirm={openCheckout}
        onCancel={modal.close}
        contentLoading={isUpcomingLoading}
        confirmLoading={subscriptionUpdate.isPending || finalizing}
      >
        {isUpcomingLoading ? (
          <></>
        ) : (
          <div className="flex flex-col mt-3 divide-y">
            <div className="flex flex-row items-center py-3">
              <Typography variant="subtitle2" className="">
                {PLAN_TO_LABEL.get(targetPlan)}
              </Typography>
              <Typography variant="subtitle2" className="ml-auto">
                {discountPercent > 0 && (
                  <span className="line-through text-slate-400 mr-1.5">
                    {formatUsd(listPriceCents)}
                  </span>
                )}
                {formatUsd(discountedPriceCents)}{" "}
                {interval === "yearly" ? "/year" : "/month"}
              </Typography>
            </div>
            {profile?.plan && getAdjustmentAmountCents() > 0 && (
              <div className="flex flex-row items-center py-3">
                <div className="flex flex-col gap-1">
                  <Typography variant="subtitle2" className="">
                    Adjustment
                  </Typography>
                  <Typography variant="caption" className="text-slate-500">
                    Prorated to end of month for your{" "}
                    {PLAN_TO_LABEL.get(profile.plan)} plan.
                  </Typography>
                </div>
                <Typography
                  variant="subtitle2"
                  className="ml-auto text-green-500"
                >
                  -{formatUsd(getAdjustmentAmountCents())}
                </Typography>
              </div>
            )}

            <div className="flex flex-row items-center py-3">
              <Typography variant="subtitle2" className="">
                Total Due Today
              </Typography>
              <Typography variant="subtitle2" className="ml-auto">
                {isPeriodStartBeforeOrToday ? (
                  formatUsd(upcoming?.invoice?.amount_due ?? 0)
                ) : (
                  <span className="text-green-500">$0</span>
                )}
              </Typography>
            </div>
          </div>
        )}
      </ConfirmAction>
    </div>
  );
};

export default ConfirmUpdatePlan;
